import { createPortal } from "react-dom";
import { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, IconButton, Typography } from "@mui/joy";
import { ArrowLeft, ChevronLeft, ChevronRight, Heart, Pencil, Trash2 } from "lucide-react";

import { AuthenticatedImage } from "..";
import { ImageWithMetadata } from "../../constants/inspectionPhotos";
import { isBlobImageUrl, toPreviewImage } from "../../utils/taskAssetImages";

interface MobileImagePreviewProps {
    images: ImageWithMetadata[];
    index: number;
    onClose: () => void;
    onIndexChange: (index: number) => void;
}

export function MobileImagePreview(props: MobileImagePreviewProps) {
    const { images, index, onClose, onIndexChange } = props;
    const image = images[index];
    const preview = image ? toPreviewImage(image) : null;
    const assetId = preview?.assetId || null;
    const previewUrl = isBlobImageUrl(preview?.url)
        ? preview?.url || null
        : null;
    const note = preview?.note || null;
    const [imageLoaded, setImageLoaded] = useState(false);
    const touchStartX = useRef(0);

    useEffect(() => {
        setImageLoaded(false);
    }, [index, assetId, previewUrl]);

    const goPrev = () => {
        if (index > 0) {
            onIndexChange(index - 1);
        }
    };

    const goNext = () => {
        if (index < images.length - 1) {
            onIndexChange(index + 1);
        }
    };

    const handleTouchStart = (event: React.TouchEvent) => {
        touchStartX.current = event.touches[0].clientX;
    };

    const handleTouchEnd = (event: React.TouchEvent) => {
        const diff = event.changedTouches[0].clientX - touchStartX.current;
        if (diff > 60) {
            goPrev();
        } else if (diff < -60) {
            goNext();
        }
    };

    if (!preview) {
        return null;
    }

    return createPortal(
        <Box
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            sx={{
                position: "fixed",
                inset: 0,
                zIndex: 1400,
                bgcolor: "#000",
                display: "flex",
                flexDirection: "column",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 1,
                    py: 1,
                    pt: "max(8px, env(safe-area-inset-top))",
                    color: "#fff",
                }}
            >
                <IconButton
                    variant="plain"
                    color="neutral"
                    onClick={onClose}
                    sx={{ color: "#fff", borderRadius: "4px" }}
                >
                    <ArrowLeft size={22} />
                </IconButton>
                <Typography
                    level="body-sm"
                    sx={{ color: "rgba(255,255,255,0.85)" }}
                >
                    {index + 1} / {images.length}
                </Typography>
                <Box sx={{ width: 40 }} />
            </Box>

            <Box
                sx={{
                    flex: 1,
                    position: "relative",
                    minHeight: 0,
                }}
            >
                {!imageLoaded && (
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            pointerEvents: "none",
                            zIndex: 1,
                        }}
                    >
                        <CircularProgress size="sm" />
                    </Box>
                )}

                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        opacity: imageLoaded ? 1 : 0,
                        transition: "opacity 0.25s ease",
                    }}
                >
                    <AuthenticatedImage
                        assetId={assetId}
                        url={previewUrl}
                        alt={`Photo ${index + 1}`}
                        objectFit="contain"
                        width="100%"
                        height="100%"
                        suppressLoadingUI
                        onLoad={() => setImageLoaded(true)}
                        sx={{
                            width: "100%",
                            height: "100%",
                            maxHeight: "100%",
                            display: "block",
                        }}
                    />
                </Box>

                {index > 0 && (
                    <IconButton
                        variant="soft"
                        color="neutral"
                        onClick={goPrev}
                        sx={{
                            position: "absolute",
                            left: 8,
                            top: "50%",
                            transform: "translateY(-50%)",
                            bgcolor: "rgba(0,0,0,0.35)",
                            color: "#fff",
                            borderRadius: "4px",
                        }}
                    >
                        <ChevronLeft size={22} />
                    </IconButton>
                )}

                {index < images.length - 1 && (
                    <IconButton
                        variant="soft"
                        color="neutral"
                        onClick={goNext}
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: "50%",
                            transform: "translateY(-50%)",
                            bgcolor: "rgba(0,0,0,0.35)",
                            color: "#fff",
                            borderRadius: "4px",
                        }}
                    >
                        <ChevronRight size={22} />
                    </IconButton>
                )}

                <Box
                    sx={{
                        position: "absolute",
                        left: "50%",
                        bottom: 12,
                        transform: "translateX(-50%)",
                        zIndex: 2,
                        display: "flex",
                        gap: 0.25,
                        px: 1,
                        py: 0.5,
                        borderRadius: "999px",
                        bgcolor: "rgba(0,0,0,0.55)",
                        backdropFilter: "blur(12px)",
                        border: "1px solid rgba(255,255,255,0.12)",
                        boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
                    }}
                >
                    <IconButton
                        variant="plain"
                        size="sm"
                        disabled
                        aria-label="Edit note"
                        sx={{
                            color: "rgba(255,255,255,0.85)",
                            borderRadius: "8px",
                        }}
                    >
                        <Pencil size={18} />
                    </IconButton>
                    <IconButton
                        variant="plain"
                        size="sm"
                        disabled
                        aria-label="Favorite photo"
                        sx={{
                            color: "rgba(255,255,255,0.85)",
                            borderRadius: "8px",
                        }}
                    >
                        <Heart size={18} />
                    </IconButton>
                    <IconButton
                        variant="plain"
                        size="sm"
                        disabled
                        aria-label="Delete photo"
                        sx={{
                            color: "rgba(255,255,255,0.85)",
                            borderRadius: "8px",
                        }}
                    >
                        <Trash2 size={18} />
                    </IconButton>
                </Box>
            </Box>

            <Box
                sx={{
                    px: 2,
                    py: 2,
                    pb: "max(16px, env(safe-area-inset-bottom))",
                    background:
                        "linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.85) 40%)",
                }}
            >
                <Typography
                    level="body-sm"
                    sx={{
                        color: note
                            ? "rgba(255,255,255,0.95)"
                            : "rgba(255,255,255,0.5)",
                        fontStyle: note ? "normal" : "italic",
                        whiteSpace: "pre-wrap",
                        textAlign: "center",
                        lineHeight: 1.5,
                    }}
                >
                    {note || "No note added for this photo."}
                </Typography>
            </Box>
        </Box>,
        document.body,
    );
}
